import api, { API_BASE, ApiError } from './api';
import {
    AiParseResult,
    AiStatus,
    DeliveryAssignee,
    DeliveryLine,
    DeliveryMeta,
    DeliveryNotificationSettings,
    DeliveryNotificationSettingsResponse,
    DeliveryOrder,
    DeliveryOrderDetail,
    DeliveryOrderPayload,
    DeliveryPacks,
    DeliveryStatus,
    LineStatus,
    ImportOrder,
    ImportOrderDetail,
    ImportOrderPayload,
    ImportStatus,
    LineCondition,
    Product,
    ProductAlias,
    ProductOptions,
    ProductPayload,
    ReturnOrder,
    ReturnOrderDetail,
    ReturnOrderMeta,
    ReturnOrderPayload,
    ReceiptStatus,
    ReturnStatus,
    StockComment,
    StockDocument,
    StockOrderLine,
    StockDashboard,
    StockReceipt,
    StockReceiptLine,
    StockSignature,
    TransferOrder,
    TransferOrderDetail,
    TransferStatus,
    Yard,
    YardDetail,
    YardMember,
    YardPayload,
    YardRole,
} from '../types/stock.types';

/**
 * STOCK module API (STOCK.md).
 *
 * Yards and Products are section-wide rather than yard-scoped, so they sit at the top
 * of /api/stock. Order types will hang off a yard once they are built.
 *
 * Yards have no Active/Inactive toggle: closing is a different thing (§9, §10) with its
 * own reason and confirmation, so it gets its own endpoints rather than a setStatus.
 */

const query = (params: Record<string, string | undefined>) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v) q.append(k, v);
    });
    const s = q.toString();
    return s ? `?${s}` : '';
};

/**
 * Fetches a server-rendered file (an export, a photo archive) with the Bearer token and
 * hands it to the browser as a download. Raw fetch rather than the api helper because
 * the body is bytes, not JSON.
 */
const downloadFile = async (path: string, filename: string) => {
    const token = localStorage.getItem('token');
    const base = API_BASE.replace(/\/$/, '');

    const response = await fetch(`${base}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
        // These endpoints explain themselves — 403 yard scoping, 404 nothing to send.
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || 'Download failed');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
};

const downloadExport = (path: string, format: 'csv' | 'excel', filename: string) =>
    downloadFile(
        `${path}${query({ format })}`,
        `${filename}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`,
    );

/* ------------------------------------------------------------------ yards */

export const yardsApi = {
    list: (params: { q?: string; status?: string; branchId?: string } = {}) =>
        api.get<Yard[]>(`/api/stock/yards${query(params)}`),

    /** Returns record counts and the canClose/canDelete/yardCodeLocked flags. */
    get: (id: number) => api.get<YardDetail>(`/api/stock/yards/${id}`),

    create: (body: YardPayload) => api.post<Yard>('/api/stock/yards', body),

    update: (id: number, body: Partial<YardPayload>) =>
        api.patch<Yard>(`/api/stock/yards/${id}`, body),

    /**
     * Close a yard (§9, §15.9). Both a reason and the literal phrase "CLOSE YARD" are
     * required, and both are re-checked server-side.
     */
    close: (id: number, closeReason: string, confirmation: string) =>
        api.post<Yard>(`/api/stock/yards/${id}/close`, { closeReason, confirmation }),

    /** Reopen a closed yard — closure is reversible when it was a mistake (§15.9). */
    reopen: (id: number) => api.post<Yard>(`/api/stock/yards/${id}/reopen`, {}),

    /** Only permitted when nothing references the yard (§9). */
    remove: (id: number) => api.delete<{ message: string }>(`/api/stock/yards/${id}`),

    export: (format: 'csv' | 'excel') =>
        downloadExport('/api/stock/yards/export', format, 'yards'),

    /* --- membership (STOCK-MENU.md §5.4) --- */

    members: (id: number) => api.get<YardMember[]>(`/api/stock/yards/${id}/members`),

    /** How much work is waiting on a Yard Manager here — see YardMembersSection. */
    approvalQueue: (id: number) =>
        api.get<{ pending: number; activeManagers: number }>(
            `/api/stock/yards/${id}/approval-queue`),

    addMember: (id: number, userId: number, yardRole: YardRole = 'member') =>
        api.post<YardMember>(`/api/stock/yards/${id}/members`, { userId, yardRole }),

    setMemberRole: (id: number, memberId: number, yardRole: YardRole) =>
        api.patch<YardMember>(`/api/stock/yards/${id}/members/${memberId}`, { yardRole }),

    /** Revokes future access only — everything they authored stays attributed to them. */
    removeMember: (id: number, memberId: number) =>
        api.delete<{ message: string }>(`/api/stock/yards/${id}/members/${memberId}`),
};

/* --------------------------------------------------------------- products */

export const productsApi = {
    list: (params: { q?: string; status?: string; category?: string } = {}) =>
        api.get<Product[]>(`/api/stock/products${query(params)}`),

    get: (id: number) => api.get<Product>(`/api/stock/products/${id}`),

    create: (body: ProductPayload) => api.post<Product>('/api/stock/products', body),

    update: (id: number, body: Partial<ProductPayload>) =>
        api.patch<Product>(`/api/stock/products/${id}`, body),

    /** Products are never deleted — inactive ones stay on historical orders (§8). */
    setStatus: (id: number, isActive: boolean) =>
        api.patch<Product>(`/api/stock/products/${id}/status`, { isActive }),

    options: () => api.get<ProductOptions>('/api/stock/products/options'),

    addAlias: (id: number, alias: string) =>
        api.post<ProductAlias>(`/api/stock/products/${id}/aliases`, { alias }),

    removeAlias: (id: number, aliasId: number) =>
        api.delete<{ message: string }>(`/api/stock/products/${id}/aliases/${aliasId}`),

    export: (format: 'csv' | 'excel') =>
        downloadExport('/api/stock/products/export', format, 'products'),
};

/* --------------------------------------------------------- return orders */

/**
 * Return Orders (STOCK.md §5).
 *
 * There is deliberately no "expected quantities" endpoint. The count is blind (§5.4) —
 * variance is reported after the fact (§5.4b), never shown during the count.
 */
export const returnsApi = {
    /** Photo slots, conditions, void codes and this user's abilities. */
    meta: () => api.get<ReturnOrderMeta>('/api/stock/returns/meta'),

    list: (params: {
        status?: string; yardId?: string; projectId?: string; assignedToId?: string; q?: string;
    } = {}) => api.get<ReturnOrder[]>(`/api/stock/returns${query(params)}`),

    get: (id: number) => api.get<ReturnOrderDetail>(`/api/stock/returns/${id}`),

    create: (body: ReturnOrderPayload) => api.post<ReturnOrder>('/api/stock/returns', body),

    update: (id: number, body: Partial<ReturnOrderPayload>) =>
        api.patch<ReturnOrder>(`/api/stock/returns/${id}`, body),

    assign: (id: number, userId?: number) =>
        api.post<ReturnOrder>(`/api/stock/returns/${id}/assign`, userId ? { userId } : {}),

    /**
     * The single status-change endpoint. The server validates the move, enforces the
     * gates and writes the audit row — the UI only proposes.
     */
    transition: (id: number, body: {
        to: ReturnStatus;
        reason?: string;
        voidReasonCode?: string;
        externalRef?: string;
    }) => api.post<ReturnOrder>(`/api/stock/returns/${id}/transition`, body),

    addLine: (id: number, body: {
        productId: number; quantity: number; condition: LineCondition; notes?: string | null;
        source?: 'ai_matched' | 'manual'; originalText?: string; aiConfidence?: number;
        aiUsageId?: number | null;
    }) => api.post<StockOrderLine>(`/api/stock/returns/${id}/lines`, body),

    updateLine: (id: number, lineId: number, body: {
        quantity?: number; condition?: LineCondition; notes?: string | null;
    }) => api.patch<StockOrderLine>(`/api/stock/returns/${id}/lines/${lineId}`, body),

    removeLine: (id: number, lineId: number) =>
        api.delete<{ message: string }>(`/api/stock/returns/${id}/lines/${lineId}`),

    /**
     * Multipart, because the file is hashed server-side for the evidence trail (§12.1).
     * Sent with fetch rather than the api helper so the browser sets the multipart
     * boundary itself.
     */
    uploadPhoto: async (id: number, stage: string, file: File, geo?: { lat?: number; lng?: number }) => {
        const token = localStorage.getItem('token');
        const form = new FormData();
        form.append('photo', file);
        form.append('stage', stage);
        // The server treats its own clock as authoritative and stores this only so a
        // disagreeing device clock can be flagged (§12.1).
        form.append('deviceTimestamp', new Date().toISOString());
        if (geo?.lat !== undefined) form.append('lat', String(geo.lat));
        if (geo?.lng !== undefined) form.append('lng', String(geo.lng));

        const response = await fetch(`${API_BASE.replace(/\/$/, '')}/api/stock/returns/${id}/photos`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: form,
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.message || 'Failed to upload photo');
        return data as { photoCount: number; minPhotos: number };
    },

    /**
     * Every captured truck photo in one ZIP (§12.1 evidence), for the Transport Manager.
     * The server reads the bucket itself, so this is one request however many photos
     * the order carries.
     */
    downloadPhotos: (id: number, orderNumber: string) =>
        downloadFile(`/api/stock/returns/${id}/photos/archive`, `${orderNumber}-photos.zip`),

    sign: (id: number, body: { signatureUrl: string; typedName: string }) =>
        api.post<StockSignature>(`/api/stock/returns/${id}/signature`, body),

    /**
     * Source paperwork (§12.4) — the storage fallback, plus the gear sheet the AI
     * parsed (source 'ai_parse') kept as evidence. Multipart like the photo upload,
     * so the browser sets the boundary itself.
     */
    uploadDocuments: async (id: number, files: File[], source: 'upload' | 'ai_parse' = 'upload') => {
        const token = localStorage.getItem('token');
        const form = new FormData();
        files.forEach((f) => form.append('documents', f));
        form.append('source', source);

        const response = await fetch(`${API_BASE.replace(/\/$/, '')}/api/stock/returns/${id}/documents`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: form,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.message || 'Failed to upload documents');
        return data as StockDocument[];
    },

    /** Remove a wrongly attached source document (§12.4); the removal is audit-logged. */
    deleteDocument: (id: number, documentId: number) =>
        api.delete<{ message: string }>(`/api/stock/returns/${id}/documents/${documentId}`),

    comment: (id: number, body: string) =>
        api.post<StockComment>(`/api/stock/returns/${id}/comments`, { body }),
};

/* --------------------------------------------------------- import orders */

/** Shared by both photo uploaders — the browser must set the multipart boundary. */
const uploadPhoto = async (path: string, file: File, extra: Record<string, string> = {}) => {
    const token = localStorage.getItem('token');
    const form = new FormData();
    form.append('photo', file);
    form.append('deviceTimestamp', new Date().toISOString());
    Object.entries(extra).forEach(([k, v]) => form.append(k, v));

    const response = await fetch(`${API_BASE.replace(/\/$/, '')}${path}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to upload photo');
    return data;
};

export const importsApi = {
    meta: () => api.get<{ voidReasonCodes: string[]; minVoidReasonLength: number }>(
        '/api/stock/imports/meta'),

    list: (params: { status?: string; yardId?: string; vendorId?: string; q?: string } = {}) =>
        api.get<ImportOrder[]>(`/api/stock/imports${query(params)}`),

    get: (id: number) => api.get<ImportOrderDetail>(`/api/stock/imports/${id}`),

    create: (body: ImportOrderPayload) => api.post<ImportOrder>('/api/stock/imports', body),

    transition: (id: number, body: { to: ImportStatus; reason?: string; voidReasonCode?: string }) =>
        api.post<ImportOrder>(`/api/stock/imports/${id}/transition`, body),

    /**
     * Add an item the vendor shipped but never ordered — the receipt-line picker only
     * offers what is already on the order, and this is the escape hatch (§6.3).
     */
    addLine: (id: number, body: {
        productId: number; quantity: number;
        source?: 'ai_matched' | 'manual'; originalText?: string; aiConfidence?: number;
        aiUsageId?: number | null;
    }) => api.post<StockOrderLine>(`/api/stock/imports/${id}/lines`, body),

    /**
     * Correct the ordered quantity of a line. The server writes an audit-trail row
     * naming the item and the before/after quantities, so edits are attributable.
     */
    updateLine: (id: number, lineId: number, body: { quantity: number }) =>
        api.patch<StockOrderLine>(`/api/stock/imports/${id}/lines/${lineId}`, body),

    /**
     * Remove a line. The server refuses once a live receipt references the product —
     * at that point the quantity is edited instead. The removal is audit-logged.
     */
    removeLine: (id: number, lineId: number) =>
        api.delete<{ message: string }>(`/api/stock/imports/${id}/lines/${lineId}`),

    /** The first receipt flips the order Open -> Receiving server-side (§6.4). */
    addReceipt: (id: number, body: {
        receiptDate: string; containerId: string; stockReceiptNumber: string; unloadedBy: number[];
    }) => api.post<StockReceipt>(`/api/stock/imports/${id}/receipts`, body),

    addReceiptLine: (id: number, receiptId: number, body: { productId: number; quantity: number }) =>
        api.post<StockReceiptLine>(`/api/stock/imports/${id}/receipts/${receiptId}/lines`, body),

    /** Correct a saved line's quantity while the receipt is still in progress. */
    updateReceiptLine: (id: number, receiptId: number, lineId: number, body: { quantity: number }) =>
        api.patch<StockReceiptLine>(`/api/stock/imports/${id}/receipts/${receiptId}/lines/${lineId}`, body),

    /** Retire one line of an in-progress receipt; the receipt itself is preserved. */
    deleteReceiptLine: (id: number, receiptId: number, lineId: number) =>
        api.delete<{ message: string }>(`/api/stock/imports/${id}/receipts/${receiptId}/lines/${lineId}`),

    uploadReceiptPhoto: (id: number, receiptId: number, file: File) =>
        uploadPhoto(`/api/stock/imports/${id}/receipts/${receiptId}/photos`, file, { stage: 'material' }),

    /** Retire one photo of an in-progress receipt; the removal is audit-logged. */
    deleteReceiptPhoto: (id: number, receiptId: number, photoId: number) =>
        api.delete<{ message: string }>(`/api/stock/imports/${id}/receipts/${receiptId}/photos/${photoId}`),

    /** role must be receipt_1 or receipt_2, and the two must be different people (§12.2). */
    signReceipt: (id: number, receiptId: number, body: {
        role: 'receipt_1' | 'receipt_2'; signatureUrl: string; typedName: string;
    }) => api.post<StockSignature>(`/api/stock/imports/${id}/receipts/${receiptId}/signature`, body),

    transitionReceipt: (id: number, receiptId: number, body: { to: ReceiptStatus; reason?: string }) =>
        api.post<StockReceipt>(`/api/stock/imports/${id}/receipts/${receiptId}/transition`, body),

    comment: (id: number, body: string) =>
        api.post<StockComment>(`/api/stock/imports/${id}/comments`, { body }),

    /**
     * Order-level source paperwork (§12.4) — the storage fallback, plus the sheet the
     * AI parsed (source 'ai_parse') kept as evidence. Multipart like the photo upload.
     */
    uploadDocuments: async (id: number, files: File[], source: 'upload' | 'ai_parse' = 'upload') => {
        const token = localStorage.getItem('token');
        const form = new FormData();
        files.forEach((f) => form.append('documents', f));
        form.append('source', source);

        const response = await fetch(`${API_BASE.replace(/\/$/, '')}/api/stock/imports/${id}/documents`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: form,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.message || 'Failed to upload documents');
        return data as StockDocument[];
    },

    /** Remove a wrongly attached source document (§12.4); the removal is audit-logged. */
    deleteDocument: (id: number, documentId: number) =>
        api.delete<{ message: string }>(`/api/stock/imports/${id}/documents/${documentId}`),
};

/* ------------------------------------------------------ delivery orders */

/**
 * Delivery Orders (STOCK.md §4, ported from gearlist-poc).
 *
 * The claim/unclaim pair is what makes two pickers on one sheet safe: the server
 * settles the race, and a losing claim comes back 409 naming who won.
 */
export const deliveriesApi = {
    meta: () => api.get<DeliveryMeta>('/api/stock/deliveries/meta'),

    list: (params: {
        status?: string; yardId?: string; projectId?: string; mine?: string; q?: string;
    } = {}) => api.get<DeliveryOrder[]>(`/api/stock/deliveries${query(params)}`),

    get: (id: number) => api.get<DeliveryOrderDetail>(`/api/stock/deliveries/${id}`),

    create: (body: DeliveryOrderPayload) => api.post<DeliveryOrder>('/api/stock/deliveries', body),

    update: (id: number, body: Partial<DeliveryOrderPayload>) =>
        api.patch<DeliveryOrder>(`/api/stock/deliveries/${id}`, body),

    transition: (id: number, body: {
        to: DeliveryStatus; reason?: string; voidReasonCode?: string; externalRef?: string;
    }) => api.post<DeliveryOrder>(`/api/stock/deliveries/${id}/transition`, body),

    /**
     * Number of packs on the shipment (advisory, no workflow gate). Only the fields sent
     * are written, and null clears one back to "not counted".
     */
    updatePacks: (id: number, body: Partial<DeliveryPacks>) =>
        api.patch<{ packs: DeliveryPacks }>(`/api/stock/deliveries/${id}/packs`, body),

    addAssignee: (id: number, userId: number) =>
        api.post<DeliveryAssignee>(`/api/stock/deliveries/${id}/assignees`, { userId }),

    removeAssignee: (id: number, assigneeId: number) =>
        api.delete<{ message: string }>(`/api/stock/deliveries/${id}/assignees/${assigneeId}`),

    addLine: (id: number, body: {
        productId: number; quantity: number;
        source?: 'ai_matched' | 'manual'; originalText?: string; aiConfidence?: number;
        aiUsageId?: number | null;
    }) => api.post<DeliveryLine>(`/api/stock/deliveries/${id}/lines`, body),

    /**
     * Correct a line — the item, the ordered quantity, or both — while the list is open
     * for edits (draft/approval, or the pick window for the crew and `edit` holders).
     * A line that has been picked or counted short is refused until it is reset on the
     * pick sheet, and the server says so.
     */
    updateLine: (id: number, lineId: number, body: { productId?: number; quantity?: number }) =>
        api.patch<DeliveryLine>(`/api/stock/deliveries/${id}/lines/${lineId}`, body),

    removeLine: (id: number, lineId: number) =>
        api.delete<{ message: string }>(`/api/stock/deliveries/${id}/lines/${lineId}`),

    /** 409 with `claimedBy` when someone else got there first. */
    claimLine: (id: number, lineId: number) =>
        api.post<{ message: string }>(`/api/stock/deliveries/${id}/lines/${lineId}/claim`, {}),

    unclaimLine: (id: number, lineId: number) =>
        api.post<{ message: string }>(`/api/stock/deliveries/${id}/lines/${lineId}/unclaim`, {}),

    pickLine: (id: number, lineId: number, body: {
        lineStatus: LineStatus; qtyPicked?: number; shortfallReason?: string;
    }) => api.patch<DeliveryLine>(`/api/stock/deliveries/${id}/lines/${lineId}/pick`, body),

    /**
     * One photo section per vehicle: no vehicleId files the frame against the order's own
     * truck (the header's fleet type), an id against that additional vehicle.
     */
    uploadPhoto: (id: number, file: File, vehicleId?: number | null) =>
        uploadPhoto(`/api/stock/deliveries/${id}/photos`, file, {
            stage: 'load',
            ...(vehicleId ? { vehicleId: String(vehicleId) } : {}),
        }),

    /**
     * Every load photo on the order in one ZIP, one folder per truck — the Transport
     * Manager's copy of the evidence. Read from the bucket server-side, so it is one
     * request however many photos the run carries.
     */
    downloadPhotos: (id: number, orderNumber: string) =>
        downloadFile(`/api/stock/deliveries/${id}/photos/archive`, `${orderNumber}-photos.zip`),

    comment: (id: number, body: string) =>
        api.post<StockComment>(`/api/stock/deliveries/${id}/comments`, { body }),

    /**
     * Source paperwork (§12.4) — the storage fallback, plus the sheet the AI parsed
     * (source 'ai_parse') kept as evidence. Multipart like the photo upload.
     */
    uploadDocuments: async (id: number, files: File[], source: 'upload' | 'ai_parse' = 'upload') => {
        const token = localStorage.getItem('token');
        const form = new FormData();
        files.forEach((f) => form.append('documents', f));
        form.append('source', source);

        const response = await fetch(`${API_BASE.replace(/\/$/, '')}/api/stock/deliveries/${id}/documents`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: form,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.message || 'Failed to upload documents');
        return data as StockDocument[];
    },

    /** Remove a wrongly attached source document (§12.4); the removal is audit-logged. */
    deleteDocument: (id: number, documentId: number) =>
        api.delete<{ message: string }>(`/api/stock/deliveries/${id}/documents/${documentId}`),
};

/**
 * Delivery notification settings (§4 workflow). One row per tenant; the six events and
 * the extra recipients per group. Registered before `/:id` on the server, hence the
 * plural-free `/settings` path.
 */
export const deliverySettingsApi = {
    get: () => api.get<DeliveryNotificationSettingsResponse>('/api/stock/deliveries/settings'),

    update: (body: Partial<Pick<DeliveryNotificationSettings, 'events' | 'additionalRecipients'>>) =>
        api.put<DeliveryNotificationSettingsResponse>('/api/stock/deliveries/settings', body),
};

/* ----------------------------------------------------------- ai parsing */

/**
 * Shared across all four order types (STOCK.md §2.5).
 *
 * `parse` saves nothing — it returns candidates for the user to review and confirm.
 * The caller writes the accepted lines through the order's own endpoint.
 */
export const stockAiApi = {
    /** Call before showing the Generate Order CTA, so §2.6's microcopy can appear instead. */
    status: () => api.get<AiStatus>('/api/stock/ai/status'),

    /**
     * `model` is the optional dropdown choice (an id from AiStatus.models); omit it and
     * the server parses with its default, currently Sonnet 5.
     */
    parse: (text: string, model?: string) =>
        api.post<AiParseResult>('/api/stock/ai/parse', model ? { text, model } : { text }),

    /**
     * Photos / a PDF of a filled-in paper sheet instead of pasted text. Multipart with a
     * repeated `files` field, sent with fetch rather than the api helper so the browser
     * sets the multipart boundary itself (same reason as the photo uploads above).
     * The files are parse-only and never stored — the server's transcription of the
     * sheet comes back as `rawInputText`. Errors carry the body's code/retryable on
     * `err.data`, same as the api helper's ApiError.
     */
    parseUpload: async (files: File[], model?: string): Promise<AiParseResult> => {
        const token = localStorage.getItem('token');
        const form = new FormData();
        files.forEach((f) => form.append('files', f));
        if (model) form.append('model', model);

        const response = await fetch(`${API_BASE.replace(/\/$/, '')}/api/stock/ai/parse-upload`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: form,
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new ApiError(data?.message || 'Failed to read the sheet', response.status, data);
        }
        return data as AiParseResult;
    },
};

/* ------------------------------------------------------------- dashboard */

export const stockDashboardApi = {
    /** Every figure is filtered server-side to the caller's yards (STOCK-MENU.md §8). */
    get: () => api.get<StockDashboard>('/api/stock/dashboard'),
};

/* ------------------------------------------------------- transfer orders */

/**
 * Transfer Orders (STOCK.md §7). Global, not yard-scoped — the list is filtered
 * server-side to transfers where you can see either end (STOCK-MENU.md §5.3).
 */
export const transfersApi = {
    meta: () => api.get<{ voidReasonCodes: string[]; minVoidReasonLength: number }>(
        '/api/stock/transfers/meta'),

    list: (params: { status?: string; direction?: 'incoming' | 'outgoing'; q?: string } = {}) =>
        api.get<TransferOrder[]>(`/api/stock/transfers${query(params)}`),

    get: (id: number) => api.get<TransferOrderDetail>(`/api/stock/transfers/${id}`),

    create: (body: { originYardId: number; destinationYardId: number }) =>
        api.post<TransferOrder>('/api/stock/transfers', body),

    /**
     * Origin-side fields are rejected once In Transit (§7.2). `arrivedAt` is the only
     * field the receiving yard may set, and only while In Transit.
     */
    update: (id: number, body: {
        vendorId?: number; departedAt?: string; destinationYardId?: number; arrivedAt?: string;
    }) => api.patch<TransferOrder>(`/api/stock/transfers/${id}`, body),

    transition: (id: number, body: {
        to: TransferStatus; reason?: string; voidReasonCode?: string; externalRef?: string;
    }) => api.post<TransferOrder>(`/api/stock/transfers/${id}/transition`, body),

    addLine: (id: number, body: { productId: number; quantity: number }) =>
        api.post<StockOrderLine>(`/api/stock/transfers/${id}/lines`, body),

    removeLine: (id: number, lineId: number) =>
        api.delete<{ message: string }>(`/api/stock/transfers/${id}/lines/${lineId}`),

    uploadPhoto: (id: number, stage: 'departure' | 'arrival', file: File) =>
        uploadPhoto(`/api/stock/transfers/${id}/photos`, file, { stage }),

    sign: (id: number, body: { role: 'departure' | 'arrival'; signatureUrl: string; typedName: string }) =>
        api.post<StockSignature>(`/api/stock/transfers/${id}/signature`, body),

    /**
     * §16.1.6 — the receiver's formal "counted different" record. Deliberately not a
     * status change and it never edits the manifest: it is evidence attached to it.
     */
    raiseDiscrepancy: (id: number, note: string) =>
        api.post<{ message: string }>(`/api/stock/transfers/${id}/discrepancy`, { note }),

    comment: (id: number, body: string) =>
        api.post<StockComment>(`/api/stock/transfers/${id}/comments`, { body }),
};
